import { motion } from "framer-motion";
import { Check } from "lucide-react";
import SponsorsBlock from "@/components/SponsorsBlock";

type Act = {
  song: string;
  also?: string[];
  artist: string;
  credit?: string;
  isHeadliner?: boolean;
};

type DayBill = {
  day: string;
  date: string;
  acts: Act[];
};

const lineup: DayBill[] = [
  {
    day: "Saturday",
    date: "July 18",
    acts: [
      { song: "Joy to the World", also: ["One", "Mama Told Me Not to Come"], artist: "Three Dog Night", credit: "Saturday headliner", isHeadliner: true },
      { song: "Biggest Part of Me", also: ["How Much I Feel"], artist: "Ambrosia" },
      { song: "Brandy (You're a Fine Girl)", artist: "Elliot Lurie", credit: "originally Looking Glass" },
      { song: "Baby Come Back", artist: "Peter Beckett", credit: "originally Player" },
      { song: "Magnet and Steel", artist: "Walter Egan" },
      { song: "I'd Really Love to See You Tonight", artist: "John Ford Coley" },
    ],
  },
  {
    day: "Friday",
    date: "July 17",
    acts: [
      { song: "Escape Artist", also: ["Step", "Tropical Illusion"], artist: "Yacht Rock Revue", credit: "Friday headliner", isHeadliner: true },
      { song: "Love Will Find a Way", also: ["Whatcha Gonna Do?"], artist: "Pablo Cruise" },
      { song: "Go Your Own Way", also: ["Dreams", "The Chain"], artist: "Seven Wonders", credit: "Fleetwood Mac tribute" },
    ],
  },
];

const ActRow = ({ song, also, artist, credit, isHeadliner }: Act) => (
  <motion.div
    className="py-7 md:py-9"
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5 }}
  >
    <p
      className={`text-yacht-charcoal leading-[1.05] italic ${
        isHeadliner ? "text-3xl md:text-5xl" : "text-2xl md:text-4xl"
      }`}
      style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400 }}
    >
      {song}
    </p>
    <p className="mt-3 text-yacht-blue text-xs md:text-sm font-bold tracking-[0.2em] uppercase">
      {artist}
      {credit && <span className="text-yacht-charcoal/50 font-medium normal-case tracking-normal italic"> · {credit}</span>}
    </p>
    {also && also.length > 0 && (
      <p className="mt-2 text-yacht-charcoal/45 text-[13px] md:text-sm italic" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
        also: {also.join(" · ")}
      </p>
    )}
  </motion.div>
);

const LineupSection = () => {
  return (
    <section id="lineup" className="relative py-20 md:py-28 px-6 bg-yacht-cream">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-yacht-blue text-xs md:text-sm font-bold tracking-[0.3em] uppercase mb-4">
            ★ The Lineup ★
          </p>
          <h2
            className="text-yacht-charcoal text-4xl md:text-6xl font-bold mb-5 leading-[1.05]"
          >
            You'll know every song.
          </h2>
          <p className="text-yacht-charcoal/70 text-base md:text-lg max-w-xl mx-auto">
            Sung by the original artists. Detroit River. July 17–18.
          </p>
        </motion.div>

        {/* Day blocks */}
        <div className="space-y-12 md:space-y-16">
          {lineup.map((day) => (
            <div key={day.day}>
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-yacht-charcoal/20" />
                <p className="text-yacht-charcoal text-xs md:text-sm font-bold tracking-[0.3em] uppercase whitespace-nowrap">
                  {day.day}, {day.date}
                </p>
                <div className="h-px flex-1 bg-yacht-charcoal/20" />
              </div>

              <div>
                {day.acts.map((act) => (
                  <ActRow key={act.artist} {...act} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer line */}
        <motion.div
          className="mt-12 md:mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-yacht-charcoal/70 text-base md:text-lg italic mb-8" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            25+ live acts. Every smooth hit you've heard a thousand times.
            <br className="hidden md:block" />
            <span className="text-yacht-charcoal/50"> Last year sold out — the Roostertail only holds so many.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a
              href="/tickets"
              className="inline-block px-8 py-4 rounded-full bg-yacht-blue text-white font-bold text-base tracking-wide uppercase transition-all hover:brightness-110"
              style={{ boxShadow: "0 4px 14px hsla(193, 52%, 47%, 0.3)" }}
            >
              Get Tickets
            </a>
            <a
              href="/lineup"
              className="inline-block px-8 py-4 rounded-full border-2 border-yacht-charcoal/20 text-yacht-charcoal font-semibold text-base tracking-wide uppercase transition-all hover:bg-yacht-charcoal/5"
            >
              Full Lineup &amp; Bios
            </a>
          </div>
        </motion.div>

        <SponsorsBlock className="mt-16 md:mt-20" />
      </div>
    </section>
  );
};

const TicketCTA = () => {
  return (
    <section className="relative py-20 md:py-28 px-6 bg-yacht-blue overflow-hidden">
      {/* Subtle texture */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative max-w-xl mx-auto text-center">
        {/* Active pill — single-day launch */}
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yacht-yellow/20 border border-yacht-yellow/50 mb-7"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="w-2 h-2 rounded-full bg-yacht-yellow animate-pulse" />
          <span className="text-yacht-yellow text-[11px] md:text-xs font-bold uppercase tracking-[0.25em]">
            Single-Day Tickets Out Now
          </span>
        </motion.div>

        {/* The price — hero (face) */}
        <motion.h2
          className="text-yacht-yellow font-bold text-6xl md:text-7xl leading-none mb-3"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.05 }}
        >
          From $149
        </motion.h2>

        <motion.p
          className="text-white/80 text-sm md:text-base font-semibold tracking-[0.25em] uppercase mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Single Day or Full Weekend
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <a
            href="/tickets"
            className="inline-block px-14 py-5 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-lg md:text-xl tracking-wide uppercase transition-all hover:brightness-110"
            style={{ boxShadow: "0 4px 18px hsla(54, 93%, 49%, 0.4)" }}
          >
            Get Tickets
          </a>
        </motion.div>

        {/* One urgency line — capacity-based, no public date */}
        <motion.p
          className="mt-7 text-white/65 text-xs md:text-sm font-medium tracking-wide"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          GA · VIP · Single-Day — Friday or Saturday. Prices rise as each batch sells through.
        </motion.p>
      </div>
    </section>
  );
};

const InfoSection = () => null;

export { InfoSection, LineupSection, TicketCTA };
